import { inject, Injectable, Injector } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TuiDialogService } from '@taiga-ui/core';
import {
	PolymorpheusComponent,
	PolymorpheusContent,
} from '@taiga-ui/polymorpheus';
import {
	DialogFrameComponent,
	DIALOG_FRAME_CONTENT,
} from '../components/dialog-frame/dialog-frame.component';
import { DialogHeaderText, DialogOptions } from '../dialogs';

@Injectable({ providedIn: 'root' })
export class DialogService {
	private readonly dialog = inject(TuiDialogService);
	private readonly transloco = inject(TranslocoService);
	private readonly injector = inject(Injector);

	/**
	 * Opens a dialog using Taiga component library and passes through a component to render
	 * in the dialog context.
	 *
	 * @note by providing a `header` value we will utilize our dialog header component and inject
	 * it into the dialog view. By providing a `label` value we will instead show just a header
	 * label in the dialog instead of the entire header component. Providing a header will take priority
	 * over providing a label.
	 *
	 * @param content the component to provide to the dialog to render
	 * @param options additional configurations to affect the dialog and it's state
	 */
	open<T = undefined>(content: PolymorpheusContent, options: DialogOptions) {
		const { header, subheader, label, data, ...taigaOptions } = options;

		if (header) {
			const getText = (value: string | DialogHeaderText) => {
				if (typeof value === 'string')
					return this.transloco.translate(value);
				if (value.translate)
					return this.transloco.translate(value.text);
				return value.text;
			};

			const frameInjector = Injector.create({
				parent: this.injector,
				providers: [
					{ provide: DIALOG_FRAME_CONTENT, useValue: content },
				],
			});

			return this.dialog.open<T>(
				new PolymorpheusComponent(DialogFrameComponent, frameInjector),
				{
					...taigaOptions,
					data: {
						headerText: getText(header),
						subheaderText: subheader
							? getText(subheader)
							: undefined,
						...data,
					},
				},
			);
		}

		if (label) {
			return this.dialog.open<T>(content, {
				...taigaOptions,
				label: this.transloco.translate(label),
				data,
			});
		}

		return this.dialog.open<T>(content, { ...taigaOptions, data });
	}
}
